import type { WaterRepository } from "../application/water-repository.js";
import type { ISODate, SyncOperation, WaterEntry } from "../domain/model.js";

const DATABASE_NAME = "yumnote-web";
const DATABASE_VERSION = 3;
const WATER_STORE = "water_entries";
const OUTBOX_STORE = "sync_operations";

type StoredWaterEntry = WaterEntry & { date: ISODate };

function createId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction was aborted"));
  });
}

export class IndexedDbWaterRepository implements WaterRepository {
  private databasePromise: Promise<IDBDatabase> | null = null;
  private readonly listeners = new Map<ISODate, Set<() => void>>();

  async getTotalMilliliters(date: ISODate): Promise<number> {
    const database = await this.open();
    const transaction = database.transaction(WATER_STORE, "readonly");
    const index = transaction.objectStore(WATER_STORE).index("date");
    const entries = await requestResult(index.getAll(IDBKeyRange.only(date)) as IDBRequest<StoredWaterEntry[]>);
    await transactionDone(transaction);
    return entries.filter(entry => !entry.deletedAt).reduce((sum, entry) => sum + (entry.milliliters ?? entry.glasses * 250), 0);
  }

  async addMilliliters(date: ISODate, milliliters: number): Promise<void> {
    const database = await this.open();
    const now = new Date().toISOString();
    const entry: StoredWaterEntry = {
      id: createId(),
      date,
      dateTime: now,
      glasses: milliliters / 250,
      milliliters,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      syncStatus: "pending",
    };
    const operation: SyncOperation = {
      id: createId(),
      entityType: "waterEntry",
      entityId: entry.id,
      mutation: "upsert",
      payload: JSON.stringify(entry),
      createdAt: now,
      attemptCount: 0,
      nextAttemptAt: now,
      lastError: null,
    };

    const transaction = database.transaction([WATER_STORE, OUTBOX_STORE], "readwrite");
    transaction.objectStore(WATER_STORE).put(entry);
    transaction.objectStore(OUTBOX_STORE).put(operation);
    await transactionDone(transaction);
    this.listeners.get(date)?.forEach(listener => listener());
  }

  async setTotalMilliliters(date: ISODate, milliliters: number): Promise<void> {
    const database = await this.open();
    const now = new Date().toISOString();
    const transaction = database.transaction([WATER_STORE, OUTBOX_STORE], "readwrite");
    const store = transaction.objectStore(WATER_STORE);
    const entries = await requestResult(store.index("date").getAll(IDBKeyRange.only(date)) as IDBRequest<StoredWaterEntry[]>);
    for (const existing of entries.filter(entry => !entry.deletedAt)) {
      const deleted = { ...existing, deletedAt: now, updatedAt: now, syncStatus: "pending" as const };
      store.put(deleted);
      transaction.objectStore(OUTBOX_STORE).put({ id: createId(), entityType: "waterEntry", entityId: deleted.id, mutation: "delete", payload: JSON.stringify(deleted), createdAt: now, attemptCount: 0, nextAttemptAt: now, lastError: null } satisfies SyncOperation);
    }
    if (milliliters > 0) {
      const entry: StoredWaterEntry = { id: createId(), date, dateTime: now, glasses: milliliters / 250, milliliters, createdAt: now, updatedAt: now, deletedAt: null, syncStatus: "pending" };
      store.put(entry);
      transaction.objectStore(OUTBOX_STORE).put({ id: createId(), entityType: "waterEntry", entityId: entry.id, mutation: "upsert", payload: JSON.stringify(entry), createdAt: now, attemptCount: 0, nextAttemptAt: now, lastError: null } satisfies SyncOperation);
    }
    await transactionDone(transaction);
    this.listeners.get(date)?.forEach(listener => listener());
  }

  subscribe(date: ISODate, listener: () => void): () => void {
    const listeners = this.listeners.get(date) ?? new Set<() => void>();
    listeners.add(listener);
    this.listeners.set(date, listeners);
    return () => {
      listeners.delete(listener);
      if (!listeners.size) this.listeners.delete(date);
    };
  }

  private open(): Promise<IDBDatabase> {
    if (!this.databasePromise) {
      this.databasePromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
        request.onupgradeneeded = () => {
          const database = request.result;
          if (!database.objectStoreNames.contains(WATER_STORE)) {
            const water = database.createObjectStore(WATER_STORE, { keyPath: "id" });
            water.createIndex("date", "date", { unique: false });
          }
          if (!database.objectStoreNames.contains(OUTBOX_STORE)) database.createObjectStore(OUTBOX_STORE, { keyPath: "id" });
          if (!database.objectStoreNames.contains("meals")) {
            const meals = database.createObjectStore("meals", { keyPath: "id" });
            meals.createIndex("date", "date", { unique: false });
          }
          if (!database.objectStoreNames.contains("meal_items")) {
            const items = database.createObjectStore("meal_items", { keyPath: "id" });
            items.createIndex("date", "date", { unique: false });
            items.createIndex("mealId", "mealId", { unique: false });
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error("Could not open IndexedDB"));
        request.onblocked = () => reject(new Error("IndexedDB upgrade is blocked by another tab"));
      });
    }
    return this.databasePromise;
  }
}

export const webWaterRepository = new IndexedDbWaterRepository();
